export interface JourneyProgress {
  uid: string;
  startedAt: string;
  updatedAt: string;
  completedAt: string | null;
  completedSteps: string[];
  lastRoute?: string;
  lastQuery?: string;
}

const journeyKey = (uid: string) => `garuda_journey_${uid}`;

function normalizeProgress(progress: Partial<JourneyProgress> & { uid: string }): JourneyProgress {
  return {
    uid: progress.uid,
    startedAt: progress.startedAt ?? new Date().toISOString(),
    updatedAt: progress.updatedAt ?? new Date().toISOString(),
    completedAt: progress.completedAt ?? null,
    completedSteps: Array.from(new Set((progress.completedSteps ?? []).filter(Boolean))),
    lastRoute: progress.lastRoute,
    lastQuery: progress.lastQuery,
  };
}

export function loadJourneyProgress(uid: string): JourneyProgress | null {
  try {
    const raw = localStorage.getItem(journeyKey(uid));
    if (!raw) return null;
    return normalizeProgress(JSON.parse(raw) as Partial<JourneyProgress> & { uid: string });
  } catch {
    return null;
  }
}

export function saveJourneyProgress(progress: JourneyProgress): void {
  localStorage.setItem(journeyKey(progress.uid), JSON.stringify(progress));
}

export function startJourney(uid: string): JourneyProgress {
  const existing = loadJourneyProgress(uid);
  if (existing) return existing;
  const now = new Date().toISOString();
  const progress = normalizeProgress({
    uid,
    startedAt: now,
    updatedAt: now,
    completedAt: null,
    completedSteps: [],
  });
  saveJourneyProgress(progress);
  return progress;
}

export function markJourneyStep(
  uid: string,
  stepId: string,
  patch: Partial<Omit<JourneyProgress, 'uid' | 'startedAt' | 'updatedAt' | 'completedSteps'>> & {
    lastRoute?: string;
    lastQuery?: string;
  } = {},
): JourneyProgress {
  const current = startJourney(uid);
  const progress = normalizeProgress({
    ...current,
    ...patch,
    uid,
    completedSteps: Array.from(new Set([...current.completedSteps, stepId])),
    updatedAt: new Date().toISOString(),
  });
  saveJourneyProgress(progress);
  return progress;
}

export function completeJourney(
  uid: string,
  patch: Partial<Omit<JourneyProgress, 'uid' | 'startedAt' | 'updatedAt' | 'completedSteps' | 'completedAt'>> = {},
): JourneyProgress {
  const current = startJourney(uid);
  const progress = normalizeProgress({
    ...current,
    ...patch,
    uid,
    completedAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });
  saveJourneyProgress(progress);
  return progress;
}
